/**
 * GameState represents the complete serializable snapshot of a Cribbage match.
 */
export class GameState {
  constructor(data = {}) {
    Object.assign(this, data);
    this.phase = data.phase || 'cut';
    this.dealer = data.dealer || 'p1';
    this.turn = data.turn || 'p2';
    this.count = Number(data.count || 0);
    this.pile = Array.isArray(data.pile) ? [...data.pile] : [];
    this.played = Array.isArray(data.played) ? [...data.played] : [];
    this.scores = data.scores ? { ...data.scores } : {};
    this.prev = data.prev ? { ...data.prev } : {};
    this.handPeg = data.handPeg ? { ...data.handPeg } : {};
    this.seatHands = data.seatHands ? { ...data.seatHands } : {};
    this.seatDiscards = data.seatDiscards ? { ...data.seatDiscards } : {};
    this.starter = data.starter || null;
    this.crib = Array.isArray(data.crib) ? [...data.crib] : [];
    this.selected = Array.isArray(data.selected) ? [...data.selected] : [];
    this.lastPlayed = data.lastPlayed || null;

    // Pause & handover flags
    this.paused = Boolean(data.paused);
    this.pausedForSign = Boolean(data.pausedForSign);
    this.pausedForAdultTimer = Boolean(data.pausedForAdultTimer);
    this.pausedForAdultStrip = Boolean(data.pausedForAdultStrip);
    this.pausedForPintsBonus = Boolean(data.pausedForPintsBonus);
    this.passingTo = data.passingTo || null;
    this.frozen = Boolean(data.frozen);
    this.over = Boolean(data.over);
  }

  isPaused() {
    return Boolean(
      this.paused ||
      this.pausedForAdultTimer ||
      this.pausedForAdultStrip ||
      this.pausedForSign ||
      this.pausedForPintsBonus
    );
  }

  resetRound() {
    this.count = 0;
    this.pile = [];
    this.played = [];
    this.crib = [];
    this.selected = [];
    this.starter = null;
    this.lastPlayed = null;
    this.passingTo = null;
    this.frozen = false;
  }

  toPlain() {
    return JSON.parse(JSON.stringify(this));
  }

  static from(obj) {
    if (!obj) return null;
    if (obj instanceof GameState) return obj;
    return new GameState(obj);
  }

  static getSeatHand(arg1, arg2 = null) {
    let seatId = typeof arg1 === 'string' ? arg1 : (typeof arg2 === 'string' ? arg2 : null);
    let state = (arg1 && typeof arg1 === 'object') ? arg1 : ((arg2 && typeof arg2 === 'object') ? arg2 : (typeof window !== 'undefined' ? window.state : null));
    if (!seatId || !state) return [];
    let sId = seatId;
    if (sId === 'you') sId = 'p1';
    if (sId === 'cpu') sId = 'p2';
    if (state.seatHands && state.seatHands[sId]) return state.seatHands[sId];
    if (state.hands && state.hands[sId]) return state.hands[sId];
    if (sId === 'p1') return state.youHand || (state.hands && state.hands.p1) || [];
    if (sId === 'p2') return state.cpuHand || (state.hands && state.hands.p2) || [];
    return [];
  }

  static setSeatHand(arg1, arg2, arg3 = null) {
    let state, seatId, cards;
    if (typeof arg1 === 'string') {
      seatId = arg1;
      cards = arg2;
      state = arg3 || (typeof window !== 'undefined' ? window.state : null);
    } else {
      state = arg1;
      seatId = arg2;
      cards = arg3;
    }
    if (!seatId || typeof seatId !== 'string' || !state) return;
    let sId = seatId;
    if (sId === 'you') sId = 'p1';
    if (sId === 'cpu') sId = 'p2';
    if (!state.seatHands) state.seatHands = {};
    state.seatHands[sId] = cards;
    if (!state.hands) state.hands = {};
    state.hands[sId] = cards;
    if (sId === 'p1') state.youHand = cards;
    if (sId === 'p2') state.cpuHand = cards;
  }

  static getSeatKept(arg1, arg2 = null) {
    let seatId = typeof arg1 === 'string' ? arg1 : (typeof arg2 === 'string' ? arg2 : null);
    let state = (arg1 && typeof arg1 === 'object') ? arg1 : ((arg2 && typeof arg2 === 'object') ? arg2 : (typeof window !== 'undefined' ? window.state : null));
    if (!seatId || !state) return [];
    let sId = seatId;
    if (sId === 'you') sId = 'p1';
    if (sId === 'cpu') sId = 'p2';
    if (state.seatKept && state.seatKept[sId]) return state.seatKept[sId];
    if (state.kept && state.kept[sId]) return state.kept[sId];
    if (sId === 'p1') return state.youKept || (state.kept && state.kept.p1) || [];
    if (sId === 'p2') return state.cpuKept || (state.kept && state.kept.p2) || [];
    return [];
  }

  static setSeatKept(arg1, arg2, arg3 = null) {
    let state, seatId, cards;
    if (typeof arg1 === 'string') {
      seatId = arg1;
      cards = arg2;
      state = arg3 || (typeof window !== 'undefined' ? window.state : null);
    } else {
      state = arg1;
      seatId = arg2;
      cards = arg3;
    }
    if (!seatId || typeof seatId !== 'string' || !state) return;
    let sId = seatId;
    if (sId === 'you') sId = 'p1';
    if (sId === 'cpu') sId = 'p2';
    if (!state.seatKept) state.seatKept = {};
    state.seatKept[sId] = cards;
    if (!state.kept) state.kept = {};
    state.kept[sId] = cards;
    if (sId === 'p1') state.youKept = cards;
    if (sId === 'p2') state.cpuKept = cards;
  }

  getSeatHand(seatId) {
    return GameState.getSeatHand(seatId, this);
  }

  setSeatHand(seatId, cards) {
    return GameState.setSeatHand(seatId, cards, this);
  }

  getSeatKept(seatId) {
    return GameState.getSeatKept(seatId, this);
  }

  setSeatKept(seatId, cards) {
    return GameState.setSeatKept(seatId, cards, this);
  }
}

